import type { CallScript } from "@frontierx/shared/schemas/CallScript";
import { ServerMessageSchema } from "@frontierx/shared/schemas/ServerMessage";
import { TranscriptUpdateSchema } from "@frontierx/shared/schemas/TranscriptUpdate";
import {
  AIQuestionAnswerSchema,
  PlaybookProgressSchema,
  PlaybookSectionSchema,
  RecallBotSchema,
  StatusChangeSchema,
} from "@frontierx/types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocalStorage } from "usehooks-ts";
import { z } from "zod";
import { useAppPartySocket } from "~/app/hooks/useAppPartySocket";
import { useFeatureFlag } from "~/app/hooks/useFeatureFlag";
import type { CompatibleSocket } from "~/app/types/CompatibleSocket";
import { logWebSocketZodError } from "~/app/utils/zodErrorFormatter";
import { useWebSocketEventLogger } from "../../hooks/useWebSocketEventLogger";
import { type BotSocketType, useCallStore } from "../../stores/callStore";
import { useAnalytics } from "../Analytics/AnalyticsProvider";
import { CallTestingPanel } from "../Testing/CallTestingPanel";
import { CallProvider, useCallContext } from "./CallContext";
import { CallDisplayUI } from "./CallDisplayUI";
import { TestPortal } from "./CallManagerWrapper";
import { ScriptSelectionModal } from "./ScriptSelectionModal";

export function CallDisplay({
  mainSocket,
  roomId,
  leaveCall,
}: Readonly<{
  mainSocket: CompatibleSocket;
  roomId: string;
  leaveCall: () => void;
}>) {
  const { trackEvent } = useAnalytics();

  // WebSocket event logging (we'll pass this to the socket handlers)
  const { logReceivedMessage, logError: logWebSocketError } =
    useWebSocketEventLogger();

  // State for script selection modal
  const [showScriptModal, setShowScriptModal] = useState(false);
  const [hasReceivedInitialData, setHasReceivedInitialData] = useState(false);

  // Debug logging
  useEffect(() => {
    console.log("[CallDisplay] Script modal state:", {
      showScriptModal,
      hasReceivedInitialData,
    });
  }, [showScriptModal, hasReceivedInitialData]);

  // Get all Zustand store actions for message handling
  const setTranscript = useCallStore((state) => state.setTranscript);
  const updateTranscriptItem = useCallStore(
    (state) => state.updateTranscriptItem,
  );
  const setRoomBot = useCallStore((state) => state.setRoomBot);
  const updateRoomBotStatus = useCallStore(
    (state) => state.updateRoomBotStatus,
  );
  const setAIHistory = useCallStore((state) => state.setAIHistory);
  const setStreamingStatus = useCallStore((state) => state.setStreamingStatus);
  const setIsStreaming = useCallStore((state) => state.setIsStreaming);
  const setStreamingResponse = useCallStore(
    (state) => state.setStreamingResponse,
  );
  const endAIStream = useCallStore((state) => state.endAIStream);
  const handleAIError = useCallStore((state) => state.handleAIError);
  const addAIHistoryItem = useCallStore((state) => state.addAIHistoryItem);
  const setMessageLatency = useCallStore((state) => state.setMessageLatency);
  const setLatencyMetrics = useCallStore((state) => state.setLatencyMetrics);
  const setError = useCallStore((state) => state.setError);
  const setPlaybook = useCallStore((state) => state.setPlaybook);
  const setPlaybookCurrentSection = useCallStore(
    (state) => state.setPlaybookCurrentSection,
  );
  const updatePlaybookSection = useCallStore(
    (state) => state.updatePlaybookSection,
  );
  const setCurrentScriptItem = useCallStore(
    (state) => state.setCurrentScriptItem,
  );
  const setQuestions = useCallStore((state) => state.setQuestions);
  const setParticipants = useCallStore((state) => state.setParticipants);
  const setParticipantRoles = useCallStore(
    (state) => state.setParticipantRoles,
  );
  const addScriptDetectionLog = useCallStore(
    (state) => state.addScriptDetectionLog,
  );

  // Get connection status actions and state
  const setPartyServerConnectionStatus = useCallStore(
    (state) => state.setPartyServerConnectionStatus,
  );
  const resetReconnectAttempt = useCallStore(
    (state) => state.resetReconnectAttempt,
  );
  const incrementReconnectAttempt = useCallStore(
    (state) => state.incrementReconnectAttempt,
  );
  const reconnectAttempt = useCallStore((state) => state.reconnectAttempt);
  const _roomBot = useCallStore((state) => state.roomBot);

  const [useRealtimeAPIRaw] = useLocalStorage("use-realtime-api", false);
  const _useRealtimeAPI: boolean = useRealtimeAPIRaw === true;
  const _hasRealtimeFeatureFlag = useFeatureFlag("realtime-api");

  // Use ref to store botSocket for reconnect function
  const botSocketRef = useRef<BotSocketType | null>(null);

  // Network event listeners
  useEffect(() => {
    const handleOnline = () => {
      console.log("Network is online. Attempting to reconnect...");
      // Network reconnection will be handled by botSocket reconnect logic
    };

    const handleOffline = () => {
      console.log("Network is offline. WebSocket may disconnect.");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Define handleReconnect function using ref
  const handleReconnect = useCallback(() => {
    setPartyServerConnectionStatus("reconnecting");

    // Simple thrashing detection - warn after 3 attempts
    if (reconnectAttempt >= 3) {
      console.warn(
        `[ConnectionThrottler] Multiple reconnection attempts (${reconnectAttempt + 1}). Consider refreshing page if this continues.`,
      );
    }

    const backoffTime = Math.min(1000 * 2 ** reconnectAttempt, 30000); // Max 30 seconds
    setTimeout(() => {
      incrementReconnectAttempt();
      if (botSocketRef.current?.reconnect) {
        botSocketRef.current.reconnect();
      }
    }, backoffTime);
  }, [
    reconnectAttempt,
    incrementReconnectAttempt,
    setPartyServerConnectionStatus,
  ]);

  // Create botSocket with complete message handling
  const botSocket: BotSocketType = useAppPartySocket({
    room: roomId,
    party: "call",
    onOpen() {
      console.log(
        "[CallDisplay] WebSocket connection opened for room:",
        roomId,
      );
      setPartyServerConnectionStatus("connected");
      resetReconnectAttempt();
    },
    onClose() {
      console.warn(
        "[CallDisplay] WebSocket connection closed for room:",
        roomId,
      );
      setPartyServerConnectionStatus("disconnected");
      handleReconnect();
    },
    onError(error) {
      console.error("[CallDisplay] WebSocket error for room:", roomId, error);
      setError("WebSocket connection error. Some features may be unavailable.");
    },
    onMessage(event) {
      // Normalize event data to string safely
      const toText = async (data: unknown): Promise<string | null> => {
        if (typeof data === "string") return data;
        if (data instanceof Blob) return await data.text();
        if (data instanceof ArrayBuffer) return new TextDecoder().decode(data);
        return null;
      };

      (async () => {
        const text = await toText(event.data);
        if (text == null) {
          console.warn("Unsupported WebSocket message type", typeof event.data);
          return;
        }

        // Handle WebSocket ping/pong frames - these are not JSON messages
        if (text === "ping") {
          botSocket.send("pong");
          return;
        }
        if (text === "pong") {
          return;
        }

        try {
          const data = JSON.parse(text);

          // Add detailed logging for debugging invalid messages
          const parseResult = ServerMessageSchema.safeParse(data);
          if (!parseResult.success) {
            console.error("Invalid message received on call websocket:");
            logWebSocketZodError(
              parseResult.error,
              data,
              "ServerMessageSchema",
            );
            logWebSocketError(
              "received",
              "INVALID_MESSAGE",
              data,
              `Schema validation failed: ${parseResult.error.message}`,
            );
            return; // Skip processing invalid messages
          }

          const message = parseResult.data;

          // Log the received WebSocket message
          logReceivedMessage(message.type, message);

          // Reduced logging - only log important message types
          if (
            message.type === "FULL_TRANSCRIPT" ||
            message.type === "BOT_INFO"
          ) {
            console.log("[CallDisplay] Received message:", message.type);
          }

          switch (message.type) {
            case "PROCESS_SCRIPT":
              // setProcessScript(message.payload); // Removed as per edit hint
              break;

            case "FULL_TRANSCRIPT":
              console.log(
                "[CallDisplay] Setting full transcript with",
                message.payload.length,
                "items",
              );
              setTranscript(
                z.array(TranscriptUpdateSchema).parse(message.payload),
              );
              break;

            case "TRANSCRIPT_UPDATE": {
              const updatedItem = TranscriptUpdateSchema.parse(message.payload);
              updateTranscriptItem(updatedItem);
              break;
            }

            case "BOT_INFO":
              setRoomBot(RecallBotSchema.parse(message.payload));
              break;

            case "STATUS_UPDATE": {
              const newStatus = StatusChangeSchema.parse(
                message.payload.status,
              );
              updateRoomBotStatus(newStatus);
              break;
            }

            case "AI_HISTORY":
              setAIHistory(message.payload);
              break;

            case "AI_HELP_STREAM_INFO":
              setStreamingStatus(message.payload);
              break;

            case "AI_HELP_STREAM":
              setIsStreaming(true);
              setStreamingResponse(message.payload);
              break;

            case "AI_ERROR": {
              handleAIError();
              break;
            }

            case "AI_HELP_STREAM_END": {
              const parsed = AIQuestionAnswerSchema.safeParse(message.payload);
              if (parsed.success) {
                addAIHistoryItem(parsed.data);
              }
              endAIStream(message.payload);
              break;
            }

            case "MESSAGE_LATENCY":
              console.log("Message Latency", message.payload);
              setMessageLatency(message.payload);
              break;

            case "AI_LATENCY_METRICS":
              setLatencyMetrics(message.payload);
              trackEvent("room_ai_latency_metrics", message.payload);
              break;

            case "ERROR":
              setError(message.payload.message);
              setIsStreaming(false);
              console.error(
                "Received error from server:",
                message.payload.message,
              );
              trackEvent("room_error", { message: message.payload.message });
              break;

            case "PLAYBOOK_PROGRESS": {
              const parsedPlaybook = PlaybookProgressSchema.parse(
                message.payload,
              );
              console.log(
                "[CallDisplay] Received PLAYBOOK_PROGRESS:",
                parsedPlaybook,
              );
              setPlaybook(parsedPlaybook);
              setHasReceivedInitialData(true);
              break;
            }

            case "CURRENT_PLAYBOOK_SECTION":
              setPlaybookCurrentSection(message.payload);
              break;

            case "PLAYBOOK_SECTION_UPDATE": {
              const updatedSection = PlaybookSectionSchema.parse(
                message.payload,
              );
              updatePlaybookSection(updatedSection);
              break;
            }

            case "CURRENT_SCRIPT_ITEM":
              setCurrentScriptItem(message.payload);
              break;

            case "CALL_QUESTIONS_UPDATE":
              setQuestions(data.payload);
              break;

            case "PARTICIPANTS":
              setParticipants(data.payload);
              break;

            case "PLAYBOOK_COMPLETE":
              // Handle playbook completion (e.g., show a completion message)
              break;

            // Ignore messages that are specifically for the FeedbackServer
            case "INITIALIZE_FEEDBACK":
            case "REJOIN_CALL":
              console.log("Ignoring server-bound message:", message.type);
              break;

            case "PARTICIPANT_ROLES_RESPONSE":
              console.log(
                "📨 Received PARTICIPANT_ROLES_RESPONSE:",
                message.payload,
              );
              setParticipantRoles(message.payload);
              break;

            case "PARTICIPANT_ROLES_UPDATED":
              console.log(
                "📨 Received PARTICIPANT_ROLES_UPDATED:",
                message.payload,
              );
              // Request full data after update
              if (botSocket?.send) {
                console.log("📤 Requesting GET_PARTICIPANT_ROLES after update");
                botSocket.send(
                  JSON.stringify({
                    type: "GET_PARTICIPANT_ROLES",
                    payload: {},
                    timestamp: new Date().toISOString(),
                  }),
                );
              }
              break;

            case "SCRIPT_DETECTION_LOG":
              console.log("📨 Received SCRIPT_DETECTION_LOG:", message.payload);
              addScriptDetectionLog(message.payload);
              break;

            case "SCRIPT_ITEM_COMPLETED":
              console.log(
                "📨 Received SCRIPT_ITEM_COMPLETED:",
                message.payload,
              );
              // This is informational only - server sends PLAYBOOK_PROGRESS for actual state updates
              break;

            default:
              console.warn(
                "Unhandled message type:",
                (message as { type: string }).type,
              );
          }
        } catch (error) {
          console.error("Failed to parse WebSocket JSON:", error);
        }
      })();
    },
  });

  // Store botSocket in ref for reconnect function
  useEffect(() => {
    botSocketRef.current = botSocket;
  }, [botSocket]);

  // Socket validation effect
  useEffect(() => {
    // Check if socket has expected properties
    if (!botSocket || typeof botSocket.send !== "function") {
      console.error("[CallDisplay] Socket failed to initialize properly");
      setError(
        "WebSocket connection failed. Real-time features may be unavailable.",
      );
      setPartyServerConnectionStatus("disconnected");
    }
  }, [botSocket, setError, setPartyServerConnectionStatus]);

  // Show script selection modal if no playbook is received after WebSocket connection
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!hasReceivedInitialData && botSocket.readyState === WebSocket.OPEN) {
        console.log(
          "[CallDisplay] WebSocket connected but no playbook received, showing script selection modal",
        );
        setShowScriptModal(true);
      } else if (!hasReceivedInitialData) {
        console.log(
          "[CallDisplay] No playbook received and WebSocket not ready:",
          { readyState: botSocket.readyState },
        );
      }
    }, 5000); // Wait 5 seconds for WebSocket connection and initial data

    return () => clearTimeout(timeout);
  }, [hasReceivedInitialData, botSocket.readyState]);

  // Handle script selection
  const handleScriptSelect = useCallback(
    async (script: CallScript) => {
      try {
        console.log("[CallDisplay] Sending script selection:", script);
        // Make direct HTTP POST to CallServer Durable Object
        const response = await fetch(
          `${__API_URL__}/parties/call/${roomId}/api/swap-script`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              type: "SWAP_SCRIPT",
              payload: { scriptId: script.id },
            }),
          },
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to swap script: ${errorText}`);
        }

        console.log(
          "[CallDisplay] Script swapped successfully via HTTP POST to CallServer",
        );
        setShowScriptModal(false);
        trackEvent("script_selected_on_join", { scriptId: script.id });
      } catch (error) {
        console.error("Error selecting script:", error);
      }
    },
    [roomId, trackEvent],
  );

  const handleScriptCancel = useCallback(() => {
    setShowScriptModal(false);
    trackEvent("script_selection_skipped");
  }, [trackEvent]);

  return (
    <>
      <ScriptSelectionModal
        isOpen={showScriptModal}
        onSelect={handleScriptSelect}
        onCancel={handleScriptCancel}
      />
      <CallProvider
        roomId={roomId}
        leaveCall={leaveCall}
        mainSocket={mainSocket}
        botSocket={botSocket}
      >
        <CallDisplayContent />
      </CallProvider>
    </>
  );
}

function CallDisplayContent() {
  const {
    reconnectAttempt: _reconnectAttempt,
    transcript,
    botSocket,
    scriptDetectionLogs,
    webSocketEvents,
    clearWebSocketEvents,
  } = useCallContext();

  return (
    <>
      <CallDisplayUI />
      <TestPortal>
        <CallTestingPanel
          botSocket={botSocket}
          transcript={transcript}
          scriptDetectionLogs={scriptDetectionLogs}
          webSocketEvents={webSocketEvents}
          onClearWebSocketEvents={clearWebSocketEvents}
        />
      </TestPortal>
    </>
  );
}
